import { useId } from "react";
// ─── F-034: template token help ────────────────────────────────
//
// Canonical token set, mirrored from the API `buildTemplateVars`
// (template-resolver.ts) so the legend can't silently drift from what the
// resolver actually substitutes. The `dossier`-only tokens (`customDisplayLabel`,
// `displayId`) come from document-repository-adapter `resolveTitle`.
export const TEMPLATE_TOKENS = [
  'documentId',
  'batchId',
  'batchName',
  'classification',
  'operatorName',
  'pageCount',
  'yyyy',
  'MM',
  'dd',
  'HH',
  'mm',
  'ss',
  'timestamp',
  'seq',
  'ext',
] as const;

export const DOSSIER_TITLE_TOKENS = ['customDisplayLabel', 'displayId', ...TEMPLATE_TOKENS] as const;

// F-034 — illustrative sample values for the live preview, keyed by the same
// canonical token set so a typed template can be previewed resolved. These are
// display-only examples (the real values come from buildTemplateVars at export);
// the date parts use a fixed sample instant for a stable, readable preview.
// `seq` is the delivery-scoped, 2-digit padded file sequence within the export
// (server default "01" — see buildTemplateVars).
export const SAMPLE_TOKEN_VALUES: Record<string, string> = {
  customDisplayLabel: "Acme Invoice 12345",
  displayId: "INV-12345",
  documentId: "doc_abc123",
  batchId: "batch_xyz789",
  batchName: "Morning Upload",
  classification: "Invoice",
  operatorName: "J. Operator",
  pageCount: "3",
  yyyy: "2026",
  MM: "06",
  dd: "17",
  HH: "14",
  mm: "05",
  ss: "30",
  timestamp: "2026-06-17T14:05:30Z",
  seq: "01",
  ext: "pdf",
};

/** Resolve `{token}` placeholders against the sample map for the live preview.
 *  Unknown tokens are left verbatim (mirrors how a mistyped token passes
 *  through at export — useful signal for the operator). */
export function resolveSampleTemplate(template: string): string {
  return template.replace(/\{(\w+)\}/g, (whole, token: string) =>
    token in SAMPLE_TOKEN_VALUES ? SAMPLE_TOKEN_VALUES[token]! : whole,
  );
}

/**
 * A template text input with a discoverable token legend + clickable insert
 * chips and a live sample preview (F-034). `tokens` defaults to the
 * filename/folder set; dossier title fields pass the extended set.
 */
export function TemplateInput({
  label,
  value,
  onChange,
  placeholder,
  tokens = TEMPLATE_TOKENS as readonly string[],
  id,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  tokens?: readonly string[];
  /** Optional id to associate the label with the input (getByLabelText, etc). */
  id?: string;
}) {
  // Always associate the label with the input — call sites that omit `id`
  // previously rendered htmlFor={undefined}, leaving the label orphaned
  // (inaccessible + getByLabelText failures).
  const autoId = useId();
  const inputId = id ?? autoId;

  // Preview the value if set, else the placeholder, so there's always something
  // illustrative once a template (or its example) contains tokens.
  const previewSource = value && value.length > 0 ? value : placeholder ?? "";
  const showPreview = /\{\w+\}/.test(previewSource);

  return (
    <div className="rb-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
      <label className="rb-label" htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        className="rb-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {showPreview && (
        <div
          data-testid="template-preview"
          style={{ fontSize: 11, color: "var(--color-text-muted)" }}
        >
          <span style={{ marginRight: 4 }}>Preview:</span>
          <span style={{ fontFamily: "monospace", color: "var(--color-text-primary)" }}>
            {resolveSampleTemplate(previewSource)}
          </span>
        </div>
      )}
      <div
        data-testid="template-token-help"
        style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}
      >
        <span style={{ fontSize: 11, color: "var(--color-text-muted)", marginRight: 4 }}>
          Insert:
        </span>
        {tokens.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => onChange(`${value ?? ""}{${t}}`)}
            title={
              t === 'seq'
                ? 'Insert {seq} — file # within delivery (currently always 01)'
                : `Insert {${t}}`
            }
            style={{
              fontSize: 11,
              fontFamily: "monospace",
              padding: "1px 6px",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: "var(--color-bg-secondary)",
              color: "var(--color-text-primary)",
              cursor: "pointer",
            }}
          >
            {`{${t}}`}
          </button>
        ))}
      </div>
    </div>
  );
}
